/**
 * `useVoiceLabels` — collapses a namespace-bound `useTranslations` translator
 * into a `VoiceLabels` object for `VoiceRecordButton`.
 *
 * Convention: the bound namespace must expose a `voice.*` sub-tree with keys
 * matching the `VoiceLabels` shape (see chatbot/agentRunner namespaces in
 * messages/*.json). Missing keys surface as next-intl's `MISSING_MESSAGE`
 * placeholder at runtime — there is no compile-time check that a given
 * namespace actually carries a complete `voice.*` sub-tree, since next-intl
 * keys the strict `Translator` type per namespace and we accept any of them.
 *
 * @module lib/voice/useVoiceLabels
 */

import { useMemo } from 'react';

import type { VoiceLabels } from './VoiceRecordButton';

// Accept any next-intl translator regardless of which namespace it's bound
// to. `(...args: never[]) => string` is a bottom-typed signature that any
// concrete translator (chatbot, agentRunner, …) is assignable to — we erase
// the namespace's compile-time key constraint internally so a single helper
// can serve every consumer.
type AnyTranslator = (...args: never[]) => string;

export function useVoiceLabels<T extends AnyTranslator>(t: T): VoiceLabels {
  return useMemo<VoiceLabels>(() => {
    const tt = t as unknown as (key: string) => string;
    return {
      stopRecording: tt('voice.stopRecording'),
      cancelRecording: tt('voice.cancelRecording'),
      transcribing: tt('voice.transcribing'),
      tooltip: tt('voice.tooltip'),
      errors: {
        notSupported: tt('voice.notSupported'),
        permissionDenied: tt('voice.permissionDenied'),
        transcriptionFailed: tt('voice.transcriptionFailed'),
        noSpeechDetected: tt('voice.noSpeechDetected'),
      },
    };
  }, [t]);
}
